import {
  Callout,
  IconButton,
  SkeletonTable,
  SkeletonText,
  Table,
} from '@dev-spendesk/grapes';
import { useState, type Dispatch } from 'react';

import { QuerySuspense } from 'src/core/common/components/QuerySuspense';
import {
  type I18nKey,
  useTranslation,
} from 'src/core/common/hooks/useTranslation';
import { useAccountingSoftwareDisplayName } from 'src/core/modules/accounting-integration/apis';
import { hasJournalsError } from 'src/core/modules/accounting-integration/models';
import {
  NotificationType,
  useNotifications,
} from 'src/core/modules/app/notifications';
import { useIntegrationStatusQuery } from 'src/core/modules/bookkeep/hooks';

import { BankJournalSelectField } from './BankJournalSelectField';
import { JournalSelectionModal } from './JournalSelectionModal';
import { type JournalModalState } from './journalModalState';
import {
  type JournalMappingType,
  type Journal,
  type JournalMapping,
} from '../../../../../accounting';
import { useCreateJournalMappingMutation } from '../../../../hooks/useCreateJournalMappingMutation';
import { useJournalsQuery } from '../../../../hooks/useJournalsQuery';
import { CellWithButton } from '../../components/CellWithIconButton/CellWithIconButton';

export const JournalsSection = () => {
  const [modalState, setModalState] = useState<JournalModalState>({
    modalKind: 'closed',
  });

  const accountingIntegrationQueryResult = useIntegrationStatusQuery();

  const queriesState = useJournalsQuery();
  const { t } = useTranslation('global');
  const { pushNotif } = useNotifications();
  const { getDisplayName } = useAccountingSoftwareDisplayName();

  const [createJournalMapping] = useCreateJournalMappingMutation();

  async function handleCreateJournalMapping(journalMapping: JournalMapping) {
    if (!journalMapping.mappedJournal) {
      return;
    }
    await createJournalMapping({
      type: journalMapping.mappingType,
      journalId: journalMapping.mappedJournal.id,
    });

    setModalState({ modalKind: 'closed' });
    pushNotif({
      type: NotificationType.Success,
      message:
        journalMapping.mappingType === 'bank_journal'
          ? t(
              'bookkeep.integrations.settings.chift.bankJournalExport.journalSuccessfulUpdate',
            )
          : t(
              'bookkeep.integrations.settings.chift.purchaseJournalExport.journalSuccessfulUpdate',
            ),
    });
  }

  return (
    <div id="journals-section">
      <div className="mb-8">
        <h5 className="text-content-primary body-m mb-8">
          {t(
            'bookkeep.integrations.settings.chift.purchaseJournalExport.title',
          )}
        </h5>
        <p className="text-content-secondary-bg-primary body-m">
          {t(
            'bookkeep.integrations.settings.chift.purchaseJournalExport.description',
          )}
        </p>
      </div>
      <QuerySuspense
        queryState={accountingIntegrationQueryResult}
        fallback={() => null}
        loading={<div />}
      >
        {(accountingIntegration) => (
          <QuerySuspense
            queryState={queriesState}
            loading={<JournalsSkeletonTable />}
            fallback={() => <JournalsErrorWarning />}
          >
            {(data) => {
              return (
                <>
                  {accountingIntegration.integration !== 'noIntegration' &&
                    accountingIntegration.integration !== 'switchInProgress' &&
                    hasJournalsError(
                      accountingIntegration.settingsValidation,
                    ) && (
                      <Callout
                        className="mt-16 mb-16"
                        title={t(
                          'bookkeep.integrations.settings.journalErrors.purchaseJournalsErrors',
                        )}
                        variant="alert"
                      />
                    )}

                  <Table<{
                    mappingType: Exclude<JournalMappingType, 'bank_journal'>;
                    mappedJournal: Journal;
                  }>
                    rowHeight="compact"
                    columns={[
                      {
                        id: 'purchase-journal-name',
                        header: t(
                          'bookkeep.integrations.settings.chift.purchaseJournalExport.purchaseJournal',
                        ),
                        width: '50%',
                        renderCell(row) {
                          const mappinType = row.mappingType;
                          return t(journalMappingTypeToI18nKey[mappinType]);
                        },
                      },
                      {
                        id: 'sage-journal-name',
                        header:
                          accountingIntegration.integration !==
                          'switchInProgress'
                            ? t(
                                'bookkeep.integrations.settings.chift.purchaseJournalExport.chiftJournal',
                                {
                                  integration: getDisplayName(
                                    accountingIntegration.integration,
                                  ),
                                },
                              )
                            : '',
                        width: '50%',
                        getCellVariant(row) {
                          return row.mappedJournal ? undefined : 'alert';
                        },
                        renderCell(row, { isRowHovered }) {
                          return (
                            <CellWithButton
                              isButtonVisible={isRowHovered}
                              button={
                                <TableButton
                                  mapping={row}
                                  setModalState={setModalState}
                                />
                              }
                            >
                              {row.mappedJournal ? (
                                `${row.mappedJournal.externalCode} - ${row.mappedJournal.externalName}`
                              ) : (
                                <em>{t('misc.none')}</em>
                              )}
                            </CellWithButton>
                          );
                        },
                      },
                    ]}
                    data={
                      data.mappings.filter(
                        (mapping) => mapping.mappingType !== 'bank_journal',
                      ) as {
                        mappingType: Exclude<
                          JournalMappingType,
                          'bank_journal'
                        >;
                        mappedJournal: Journal;
                      }[]
                    }
                    footer={
                      data.availableJournals.length === 0 ? (
                        <TableFooter journals={data.availableJournals} />
                      ) : undefined
                    }
                  />

                  <JournalSelectionModal
                    modalState={modalState}
                    setModalState={setModalState}
                    availableJournals={data.availableJournals}
                    initialJournal={
                      modalState.modalKind === 'mapped'
                        ? data.mappings.find(
                            (journalMapping) =>
                              journalMapping.mappingType ===
                              modalState.mappingType,
                          )?.mappedJournal
                        : undefined
                    }
                    onCreate={(journalMapping: JournalMapping) =>
                      handleCreateJournalMapping(journalMapping)
                    }
                  />

                  <BankJournalSelectField
                    accountingIntegration={accountingIntegration}
                    availableJournals={data.availableJournals}
                    initialJournal={
                      data.mappings.find(
                        (mapping) => mapping.mappingType === 'bank_journal',
                      )?.mappedJournal
                    }
                    onCreate={(journalMapping: JournalMapping) =>
                      handleCreateJournalMapping(journalMapping)
                    }
                  />
                  {accountingIntegration.integration === 'Sage100' &&
                    accountingIntegration.settingsValidation.journalBank && (
                      <div className="mt-8">
                        <Callout
                          title={t(
                            'bookkeep.integrations.settings.chift.bankJournalExport.notMapped',
                          )}
                          variant="warning"
                        />
                      </div>
                    )}
                </>
              );
            }}
          </QuerySuspense>
        )}
      </QuerySuspense>
      <div className="separator my-24" />
    </div>
  );
};

const TableFooter = ({ journals }: { journals: Journal[] }) => {
  const { t } = useTranslation('global');

  if (journals.length === 0) {
    return (
      <em>
        {t(
          'bookkeep.integrations.settings.chift.purchaseJournalExport.noJournals',
        )}
      </em>
    );
  }

  return null;
};

interface TableButtonProps {
  mapping: JournalMapping;
  setModalState: Dispatch<JournalModalState>;
}

const TableButton = ({ mapping, setModalState }: TableButtonProps) => {
  const { t } = useTranslation('global');
  return (
    <span className="CellWithButton__button">
      <IconButton
        iconName="pen"
        variant="tertiaryNeutral"
        onClick={() => {
          setModalState({
            modalKind: mapping.mappedJournal ? 'mapped' : 'map',
            ...mapping,
          });
        }}
        aria-label={t('misc.edit')}
      />
    </span>
  );
};

const JournalsSkeletonTable = () => (
  <SkeletonTable
    columns={[
      {
        cell: <SkeletonText width="80%" />,
        header: <SkeletonText width="50%" />,
        width: '50%',
      },
      {
        cell: <SkeletonText width="80%" />,
        header: <SkeletonText width="50%" />,
        width: '50%',
      },
    ]}
    withColumnSeparator
    withHeader
    numberOfRows={2}
  />
);

const JournalsErrorWarning = () => {
  const { t } = useTranslation('global');

  return (
    <Callout
      title={t(
        'bookkeep.integrations.settings.chift.purchaseJournalExport.errorLoading',
      )}
      variant="alert"
    />
  );
};

const journalMappingTypeToI18nKey: Record<
  Exclude<JournalMappingType, 'bank_journal'>,
  I18nKey
> = {
  invoice:
    'bookkeep.integrations.settings.chift.purchaseJournalExport.purchaseJournalTypeName.invoice',
  expense_claim:
    'bookkeep.integrations.settings.chift.purchaseJournalExport.purchaseJournalTypeName.expense_claim',
  card_payment:
    'bookkeep.integrations.settings.chift.purchaseJournalExport.purchaseJournalTypeName.card_payment',
};
